/* =============================================================================
   [KI] PDP Swap — fetch-and-swap navigation for format toggles
   -----------------------------------------------------------------------------
   Single-purpose module: given a sibling product URL, replace #main-content
   in place with the sibling's #main-content, update the page <head>, flip
   <body data-format>, replaceState the URL, and notify listeners. The page
   never unloads. Cart drawer, header, footer, and scroll position survive.

   Public API (attached to window):
     window.kiPdpSwap.swap(url, opts)
       Performs the swap. Returns a Promise that resolves to:
         { ok: true,  format }  on success
         { ok: false, reason }  on a handled failure that did NOT navigate
       On unhandled failure (network/timeout/parse) it falls back to a real
       navigation: window.location.href = url. The promise never rejects.

     window.kiPdpSwap.prefetch(url)
       Fires a low-priority background fetch for the URL. Result sits in
       the HTTP cache. Safe to call repeatedly — browser dedupes in-flight
       requests, and the cache handles duplicates after that.

   Events dispatched on `document`:
     'pdp:swap-start'   — before fetch begins. detail: { url }
     'pdp:swap-success' — after DOM has been swapped and head updated.
                          detail: { url, format, fromFormat }
     'pdp:swap-error'   — handled error path (also fires before fallback nav).
                          detail: { url, reason }

   Why a separate module:
     The swap logic is reusable. If we later want to fetch-and-swap on
     other variant axes (e.g. flavor on a different product type), we
     don't duplicate fetch/parse/swap plumbing. Also keeps the protocol
     selector's JS readable: the format-toggle handler stays a small
     "decide what to do" function rather than mixing decision and network
     plumbing.
   ============================================================================= */

(function () {
  'use strict';

  if (window.kiPdpSwap) return; // idempotent — defensive against double-include

  // -------------------------------------------------------------------------
  // Configuration
  // -------------------------------------------------------------------------

  // Total fetch timeout before we abandon and fall back to a real navigation.
  // 4000ms chosen per handover spec. On a healthy connection a Shopify PDP
  // HTML response lands in 200–800ms; 4s is a generous ceiling that catches
  // genuine network problems without firing on slow-but-working mobile.
  var FETCH_TIMEOUT_MS = 4000;

  // The DOM region we swap. Theme.liquid renders <div role="main" id="main-content">
  // around content_for_layout. Everything PDP-specific (hero, protocol selector,
  // product facts, JSON-LD schema) lives inside this. Swap-by-id, not swap-by-tag,
  // because some themes don't use a literal <main> element.
  var MAIN_SELECTOR = '#main-content';

  // -------------------------------------------------------------------------
  // Internal state
  // -------------------------------------------------------------------------

  // Tracks the most recent in-flight swap so a fast double-tap doesn't
  // race two swaps against each other (second one would land on top of
  // the first, which is fine, but we'd fire two analytics events).
  var inFlightUrl = null;

  // -------------------------------------------------------------------------
  // Public: prefetch
  // -------------------------------------------------------------------------

  function prefetch(url) {
    if (!url) return;
    try {
      // priority: 'low' is a hint that tells the browser this is background
      // work and shouldn't compete with critical resources. Supported in
      // Chrome 102+, Safari 17.2+, Firefox 132+. Older browsers ignore it
      // and treat the fetch as default priority — still works, just not
      // optimally prioritized.
      fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        priority: 'low',
        headers: { 'X-KI-Prefetch': '1' }
      }).catch(function () {
        // Prefetch failures are non-fatal by definition. The real swap
        // will retry the fetch and either succeed or fall back to nav.
      });
    } catch (e) {
      // Some older browsers throw on unrecognized fetch options. Retry
      // without `priority` so we still warm the cache.
      try {
        fetch(url, { method: 'GET', credentials: 'same-origin' }).catch(function () {});
      } catch (e2) { /* give up silently */ }
    }
  }

  // -------------------------------------------------------------------------
  // Public: swap
  // -------------------------------------------------------------------------

  function swap(url, opts) {
    opts = opts || {};
    var fromFormat = document.body.getAttribute('data-format') || null;

    // Guard: if a swap to this exact URL is already in flight, no-op.
    if (inFlightUrl === url) {
      return Promise.resolve({ ok: false, reason: 'already-in-flight' });
    }
    inFlightUrl = url;

    dispatch('pdp:swap-start', { url: url });

    return fetchWithTimeout(url, FETCH_TIMEOUT_MS)
      .then(function (response) {
        if (!response.ok) {
          // 4xx/5xx — we treat as fatal-for-swap and fall through to nav.
          // The destination page might render its own error or 404 chrome,
          // which is the merchant's job to handle, not ours.
          throw new Error('http-' + response.status);
        }
        return response.text();
      })
      .then(function (html) {
        // Parse the response into a detached Document. parseFromString
        // doesn't execute <script> tags inside the parsed tree — they're
        // present but inert. That's important: any inline analytics or
        // section-init scripts in the fetched HTML won't double-fire.
        // We re-init what we need explicitly.
        var doc = new DOMParser().parseFromString(html, 'text/html');

        var newMain = doc.querySelector(MAIN_SELECTOR);
        var oldMain = document.querySelector(MAIN_SELECTOR);
        if (!newMain || !oldMain) {
          throw new Error('main-not-found');
        }

        // Read the new format from the parsed <body>. This is the source
        // of truth — the metafield-rendered attribute on the destination
        // page tells us what format we're swapping TO.
        var newBody = doc.body;
        var newFormat = newBody ? newBody.getAttribute('data-format') : null;

        // ---- Swap the main content ----
        // replaceWith swaps the entire #main-content node, including its
        // own attributes. Cleaner than innerHTML (preserves the new node's
        // identity) and safer (no parsing surprises).
        oldMain.replaceWith(newMain);

        // ---- Update <head> ----
        updateHead(doc);

        // ---- Update <body data-format> ----
        // This single attribute change cascades to every [data-format-only]
        // CSS rule on the page. Verzehrempfehlung swaps automatically;
        // any future format-aware copy added later will too.
        if (newFormat) {
          document.body.setAttribute('data-format', newFormat);
        } else {
          document.body.removeAttribute('data-format');
        }

        // ---- Update history ----
        // replaceState (NOT pushState) per the design decision: format
        // swap is lateral movement, not a new pageview. Back button takes
        // the customer wherever they came from (listing, ad, etc.) — not
        // to the previous format. Aligns with the GA4 event model below
        // (custom event, not page_view).
        //
        // Strip any #protocol-selector hash before writing to the URL bar.
        // The sibling URLs carry that hash so the FALLBACK navigation path
        // (real nav, no swap) lands the customer at the selector instead
        // of the page top. On a successful swap there's no scroll jump
        // happening — the customer's existing scroll position is fine —
        // and we don't want #protocol-selector persisting into the URL
        // they might bookmark or share.
        var historyUrl = url.replace(/#protocol-selector$/, '');
        try {
          history.replaceState(
            { kiSwap: true, format: newFormat, ts: Date.now() },
            '',
            historyUrl
          );
        } catch (e) {
          // SecurityError if URL is cross-origin (shouldn't happen — siblings
          // are same-origin) or if URL is malformed. Non-fatal: the DOM
          // is already swapped, the URL bar just doesn't update.
        }

        // ---- Re-init swapped sections ----
        // Sections that bind their own JS need to re-bind to the freshly-
        // inserted nodes. Two paths:
        //   1. Direct calls for sections we own and know need re-init.
        //   2. A `pdp:swapped` custom event for everything else — any
        //      section can listen and re-init on its own schedule.
        // Direct calls go FIRST so the protocol selector's format toggle
        // is bound before the user can interact with the new page.
        reinitKnownSections();

        // ---- Notify ----
        dispatch('pdp:swap-success', {
          url: url,
          format: newFormat,
          fromFormat: fromFormat
        });
        // Generic event for sections to listen to without depending on
        // this module's API. Same payload, different name for clarity:
        // `pdp:swapped` reads as "a swap happened, react if relevant."
        dispatch('pdp:swapped', {
          url: url,
          format: newFormat,
          fromFormat: fromFormat
        });

        // ---- GA4 ----
        // Custom event, not page_view. See module header for rationale.
        sendAnalytics(fromFormat, newFormat, url);

        inFlightUrl = null;
        return { ok: true, format: newFormat };
      })
      .catch(function (err) {
        // Any failure in the fetch/parse/swap pipeline lands here. The
        // contract is: we never leave the customer stranded. Real
        // navigation as fallback — they still get to the destination,
        // they just experience Option 1 behavior for this one click.
        var reason = (err && err.message) ? err.message : 'unknown';
        dispatch('pdp:swap-error', { url: url, reason: reason });
        inFlightUrl = null;

        // Best-effort GA4 ping for the fallback path so we can monitor
        // swap success rate in production. If <95% of select_format
        // events have method=fetch_swap, something is wrong.
        sendAnalytics(fromFormat, null, url, 'navigation_fallback');

        window.location.href = url;
        return { ok: false, reason: reason };
      });
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  function fetchWithTimeout(url, ms) {
    // AbortController for the timeout. If the browser doesn't support it
    // (very old), we fall back to a plain fetch with no timeout — slow
    // mobile users will eventually get the response or the fallback nav,
    // just less gracefully.
    if (typeof AbortController === 'undefined') {
      return fetch(url, { credentials: 'same-origin' });
    }
    var ctrl = new AbortController();
    var timer = setTimeout(function () { ctrl.abort(); }, ms);
    return fetch(url, {
      credentials: 'same-origin',
      signal: ctrl.signal,
      headers: { 'X-KI-Swap': '1' }
    }).then(
      function (r) { clearTimeout(timer); return r; },
      function (e) { clearTimeout(timer); throw e; }
    );
  }

  // Update the parts of <head> that are PDP-specific. We're deliberately
  // narrow here — only the elements that affect SEO, sharing, or browser
  // chrome. Stylesheet and script tags stay (they're identical between
  // sibling products, and re-injecting them would force a re-parse).
  function updateHead(newDoc) {
    var newHead = newDoc.head;
    if (!newHead) return;

    // <title>
    var newTitle = newHead.querySelector('title');
    if (newTitle && newTitle.textContent) {
      document.title = newTitle.textContent;
    }

    // <link rel="canonical">
    var newCanonical = newHead.querySelector('link[rel="canonical"]');
    var oldCanonical = document.head.querySelector('link[rel="canonical"]');
    if (newCanonical && oldCanonical) {
      oldCanonical.setAttribute('href', newCanonical.getAttribute('href'));
    }

    // <meta name="description">
    var newDesc = newHead.querySelector('meta[name="description"]');
    var oldDesc = document.head.querySelector('meta[name="description"]');
    if (newDesc && oldDesc) {
      oldDesc.setAttribute('content', newDesc.getAttribute('content') || '');
    } else if (newDesc && !oldDesc) {
      // Edge case: source page has no description but destination does.
      // Insert a fresh node so social sharing of the swapped URL works.
      document.head.appendChild(newDesc.cloneNode(true));
    }

    // Open Graph + Twitter cards. These matter when a customer shares the
    // URL after swapping — without an update, the share preview shows the
    // wrong product. We update them by name; if the destination doesn't
    // have a given tag, we leave the existing one alone (defensive).
    var socialMetaSelectors = [
      'meta[property="og:title"]',
      'meta[property="og:description"]',
      'meta[property="og:image"]',
      'meta[property="og:url"]',
      'meta[name="twitter:title"]',
      'meta[name="twitter:description"]',
      'meta[name="twitter:image"]'
    ];
    socialMetaSelectors.forEach(function (sel) {
      var src = newHead.querySelector(sel);
      var dst = document.head.querySelector(sel);
      if (src && dst) {
        var attr = src.hasAttribute('content') ? 'content' : null;
        if (attr) dst.setAttribute(attr, src.getAttribute(attr));
      }
    });
  }

  function reinitKnownSections() {
    // Protocol selector — exposes a global init via its IIFE pattern.
    // The selector's setupForm/setupFormatToggle have a data-bound guard
    // so re-running init() on already-bound nodes is a no-op. Freshly-
    // swapped nodes have no guard flag and get bound cleanly.
    if (window.kiProtocolSelector && typeof window.kiProtocolSelector.init === 'function') {
      try { window.kiProtocolSelector.init(); } catch (e) { /* best-effort */ }
    }

    // Product detail — the hero CTA scroll-to handler. Same idempotent
    // pattern (re-binds only fresh nodes thanks to the way listeners are
    // attached to specific elements; old nodes are GC'd with the swap).
    if (window.kiProductDetail && typeof window.kiProductDetail.init === 'function') {
      try { window.kiProductDetail.init(); } catch (e) { /* best-effort */ }
    }
  }

  function sendAnalytics(fromFormat, toFormat, url, methodOverride) {
    // GA4 custom event. Not a page_view — see module header.
    // Wrapped in a typeof check so missing gtag (preview themes, dev
    // stores, ad-blocker users) doesn't break the swap.
    if (typeof window.gtag !== 'function') return;
    try {
      window.gtag('event', 'select_format', {
        format_from: fromFormat || 'unknown',
        format_to: toFormat || 'unknown',
        method: methodOverride || 'fetch_swap',
        // product_pair derived from URL slug — best-effort. Format pairs
        // share a slug stem (e.g. /products/multi-powder, /products/multi-capsules).
        // Strip the trailing -powder/-capsules to get the pair name.
        product_pair: derivePair(url)
      });
    } catch (e) { /* never let analytics break the page */ }
  }

  function derivePair(url) {
    try {
      var path = new URL(url, window.location.origin).pathname;
      var slug = path.replace(/^\/products\//, '').replace(/\/$/, '');
      // Strip common format suffixes. If naming convention diverges from
      // -powder/-capsules later, this returns the raw slug — still useful
      // as a pair identifier, just less clean.
      return slug
        .replace(/-(powder|pulver|capsules|kapseln)$/i, '')
        .toLowerCase() || 'unknown';
    } catch (e) {
      return 'unknown';
    }
  }

  function dispatch(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, {
        detail: detail || {},
        bubbles: true
      }));
    } catch (e) {
      // CustomEvent constructor not available (very old browsers).
      // Skip — listeners will just not fire. Non-fatal.
    }
  }

  // -------------------------------------------------------------------------
  // Export
  // -------------------------------------------------------------------------

  window.kiPdpSwap = {
    swap: swap,
    prefetch: prefetch
  };
})();
